import { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faRotate, faSpinner, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { faSlack } from '@fortawesome/free-brands-svg-icons';
import { messagingMonitor } from '@/services/messaging-monitor';
import { getSlackSettings, getRecentSnapshots, getMessagesByDateRange, clearCursors } from '@/services/messaging-storage';
import { toLocalDateStr, subDays } from '@/lib/utils';
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import type { MessagingSnapshot, SlackSettings } from '@/services/messaging-types';
import { PlatformDashboard, cx } from './messaging-shared';

interface SlackViewProps {
  inline?: boolean;
  onClose?: () => void;
  onOpenSettings?: () => void;
  reviewSnapshot?: MessagingSnapshot;
}

export function SlackView({ inline = false, onClose, onOpenSettings, reviewSnapshot }: SlackViewProps) {
  const [settings, setSettings] = useState<SlackSettings | null>(null);
  const [snapshots, setSnapshots] = useState<MessagingSnapshot[]>([]);
  const [monitoring, setMonitoring] = useState(messagingMonitor.isRunning);
  const [polling, setPolling] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [lookbackDays, setLookbackDays] = useState(7);
  const [customSnapshot, setCustomSnapshot] = useState<MessagingSnapshot | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const alive = useRef(true);

  const load = useCallback(async () => {
    const [sl, snaps] = await Promise.all([getSlackSettings(), getRecentSnapshots('slack', 7)]);
    if (!alive.current) return;
    setSettings(sl);
    setSnapshots(snaps);
    setLastUpdated(new Date());
  }, []);

  const loadHistoricalData = useCallback(async (days: number) => {
    if (!settings || days <= 7) {
      setCustomSnapshot(null);
      return;
    }
    setLoadingHistory(true);
    try {
      const toDate = toLocalDateStr();
      const fromDate = toLocalDateStr(subDays(new Date(), days - 1));
      const messages = await getMessagesByDateRange('slack', fromDate, toDate);
      
      // Build snapshot from historical messages
      const { slackService } = await import('@/services/slack-service');
      const snapshot = slackService.buildSnapshot(messages, toDate);
      setCustomSnapshot({ ...snapshot, anomalies: [] });
    } catch (err) {
      console.error('Failed to load historical data:', err);
      setCustomSnapshot(null);
    } finally {
      setLoadingHistory(false);
    }
  }, [settings]);

  useEffect(() => {
    alive.current = true;
    load();
    const off = messagingMonitor.on((evt) => {
      if (evt === 'poll_complete') { load(); setPolling(false); }
    });
    return () => { alive.current = false; off(); };
  }, [load]);

  useEffect(() => {
    loadHistoricalData(lookbackDays);
  }, [lookbackDays, loadHistoricalData]);

  const toggleMonitor = async () => {
    if (monitoring) { messagingMonitor.stop(); setMonitoring(false); }
    else { setPolling(true); await messagingMonitor.start(); setMonitoring(true); setPolling(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    await messagingMonitor.pollAll(['slack']);
    await load();
    setPolling(false);
  };

  const resetAndRefetch = async () => {
    setPolling(true);
    await clearCursors('slack');
    await messagingMonitor.pollAll(['slack']);
    await load();
    setPolling(false);
  };

  const connected = !!(settings?.enabled && settings.accessToken);
  const isReviewing = !!reviewSnapshot;

  return (
    <div className={inline ? 'flex flex-col h-full' : 'fixed inset-0 z-[100] flex flex-col bg-gray-50 dark:bg-slate-900'}>
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700/50 bg-white dark:bg-slate-900">
        <div className="h-14 flex items-center justify-between px-5 z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#4A154B]/10 flex items-center justify-center">
              <FontAwesomeIcon icon={faSlack} className="h-4 w-4 text-[#E01E5A]" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Slack</h1>
              <p className="text-[10px] text-gray-400 dark:text-slate-500">
                {connected ? 'Connected · polls every 5 min' : 'Not connected'}
                {lastUpdated && ` · updated ${lastUpdated.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`}
              </p>
            </div>
            {connected && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-medium border border-emerald-200 dark:border-emerald-500/30">
                Connected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {connected && (
              <>
                <button onClick={pollNow} disabled={polling}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50">
                  <FontAwesomeIcon icon={polling ? faSpinner : faRotate} className={cx('h-3 w-3', polling ? 'animate-spin' : '')} />
                  Poll now
                </button>
                <button onClick={resetAndRefetch} disabled={polling}
                  title="Clear channel cursors and re-fetch all messages from scratch"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                  <FontAwesomeIcon icon={polling ? faSpinner : faRotate} className={cx('h-3 w-3', polling ? 'animate-spin' : '')} />
                  Refetch all
                </button>
                <button onClick={toggleMonitor}
                  className={cx('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border',
                    monitoring
                      ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                      : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:bg-gray-200 dark:hover:bg-slate-700')}>
                  <span className={cx('w-1.5 h-1.5 rounded-full flex-shrink-0', monitoring ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400')} />
                  {monitoring ? 'Monitoring' : 'Start monitoring'}
                </button>
              </>
            )}
            <button onClick={onOpenSettings} title="Slack Settings"
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
              <FontAwesomeIcon icon={faGear} className="h-4 w-4" />
            </button>
            {!inline && onClose && (
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Date Range Selector */}
        {connected && (
          <div className="px-5 py-2 border-t border-gray-100 dark:border-slate-700/30 flex items-center gap-3">
            <FontAwesomeIcon icon={faCalendar} className="h-3 w-3 text-gray-400" />
            <div className="flex items-center gap-1.5">
              {[7, 14, 30, 90].map(days => (
                <button
                  key={days}
                  onClick={() => setLookbackDays(days)}
                  className={cx(
                    'px-2.5 py-1 text-[10px] font-medium rounded transition-colors',
                    lookbackDays === days
                      ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400'
                      : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                  )}
                >
                  {days === 7 ? '7d' : days === 14 ? '2w' : days === 30 ? '30d' : '90d'}
                </button>
              ))}
            </div>
            <span className="text-[10px] text-gray-400 dark:text-slate-500 ml-1">
              {loadingHistory ? 'Loading...' : `Last ${lookbackDays} days`}
            </span>
          </div>
        )}
      </div>

      {/* Review mode banner */}
      {isReviewing && (
        <div className="flex-shrink-0 flex items-center gap-2 px-5 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs">
          <FontAwesomeIcon icon={faHistory} className="h-3 w-3" />
          <span>Viewing archived snapshot from <strong>{new Date(reviewSnapshot.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</strong>. Live data paused.</span>
          {onClose && <button onClick={onClose} className="ml-auto underline hover:no-underline">Exit review</button>}
        </div>
      )}

      {/* Dashboard */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <PlatformDashboard
          platformLabel="Slack"
          platform="slack"
          tokenHint="Add your Slack bot token (xoxb-...) in Settings → Integrations → Slack to start monitoring channels."
          settings={settings}
          snapshots={isReviewing ? [reviewSnapshot] : (customSnapshot ? [customSnapshot] : snapshots)}
          lookbackDays={isReviewing ? 1 : lookbackDays}
          onOpenSettings={onOpenSettings}
        />
      </div>
    </div>
  );
}
